import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { Check, Plus, RefreshCcw } from 'lucide-react';
import type { DayPlanItem } from '@4zen/api-client';
import { Badge, Button, EmptyState, ProgressBar, Skeleton } from '@4zen/ui';
import { useToast } from '@/components/Toast';
import { formatDuration, formatTime, todayIso } from '@/lib/format';
import { useAuth } from '@/stores/auth';
import { TaskCard } from '@/features/tasks/TaskCard';
import { TaskComposer } from '@/features/tasks/TaskComposer';
import { useCompleteTask } from '@/features/tasks/queries';
import { SuggestionPrompt } from './SuggestionPrompt';
import { useDayPlan, useRegenerateDayPlan, useReorderDayPlan } from './queries';

const SOURCE_LABELS: Record<DayPlanItem['source'], string> = {
  scheduled: '',
  carried_over: 'Перенесено з учора',
  pulled_forward: 'Взято наперед',
  user_choice: 'Ваш вибір',
};

export function MyDayPage() {
  const toast = useToast();
  const { user } = useAuth();
  const today = todayIso();
  const { data: plan, isLoading } = useDayPlan(today);
  const regenerate = useRegenerateDayPlan();
  const reorder = useReorderDayPlan();
  const complete = useCompleteTask();

  const [composing, setComposing] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const open = useMemo(
    () =>
      (plan?.items ?? []).filter(
        (item) => item.task.status !== 'done' && item.task.status !== 'not_done',
      ),
    [plan],
  );
  const closed = useMemo(
    () =>
      (plan?.items ?? []).filter(
        (item) => item.task.status === 'done' || item.task.status === 'not_done',
      ),
    [plan],
  );

  const current = open[0];
  const progress = plan && plan.planned_minutes > 0
    ? Math.min(100, (plan.completed_minutes / plan.planned_minutes) * 100)
    : 0;

  async function dropOn(targetTaskId: string) {
    const sourceId = draggedId;
    setDraggedId(null);
    if (!sourceId || !plan || sourceId === targetTaskId) return;

    const ids = open.map((item) => item.task_id).filter((id) => id !== sourceId);
    const insertAt = ids.indexOf(targetTaskId);
    ids.splice(insertAt < 0 ? ids.length : insertAt, 0, sourceId);
    await reorder.mutateAsync({ planId: plan.id, taskIds: ids });
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-6">
      <header className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h1 className="text-xl font-semibold">
            Мій день, {user?.full_name?.split(' ')[0] ?? ''}
          </h1>
          <p className="text-sm text-content-subtle">
            {format(new Date(), 'EEEE, d MMMM', { locale: uk })}
          </p>
        </div>
        <Button
          variant="ghost"
          icon={<RefreshCcw className="h-4 w-4" />}
          loading={regenerate.isPending}
          onClick={async () => {
            await regenerate.mutateAsync({ date: today });
            toast.success('План дня оновлено');
          }}
        >
          Перебудувати план
        </Button>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setComposing(true)}>
          Задача
        </Button>
      </header>

      {plan ? (
        <section className="zen-panel space-y-2 p-4">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
            <span className="font-medium">
              {closed.length} із {plan.items.length} виконано
            </span>
            <span className="text-content-muted">
              Заплановано {formatDuration(plan.planned_minutes)} з{' '}
              {formatDuration(plan.capacity_minutes)}
            </span>
            <span className="ml-auto text-content-subtle">
              Вільно {formatDuration(plan.remaining_minutes)}
            </span>
          </div>
          <ProgressBar value={progress} />
        </section>
      ) : null}

      {plan?.suggestion_batch ? <SuggestionPrompt batch={plan.suggestion_batch} /> : null}

      {current ? (
        <section className="rounded border border-accent bg-surface p-4">
          <p className="mb-2 text-xs uppercase tracking-wide text-content-subtle">
            Зараз у фокусі
          </p>
          <h2 className="text-lg font-medium">{current.task.title}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-content-muted">
            {current.slot_start ? (
              <span>
                {formatTime(current.slot_start)}–{formatTime(current.slot_end)}
              </span>
            ) : (
              <span>{formatDuration(current.allocated_minutes)}</span>
            )}
            {current.task.client_name ? <Badge tone="neutral">{current.task.client_name}</Badge> : null}
            {SOURCE_LABELS[current.source] ? (
              <Badge tone="info">{SOURCE_LABELS[current.source]}</Badge>
            ) : null}
          </div>
          <Button
            className="mt-3"
            icon={<Check className="h-4 w-4" />}
            loading={complete.isPending}
            onClick={async () => {
              await complete.mutateAsync({ id: current.task_id });
              toast.success('Готово');
            }}
          >
            Виконано
          </Button>
        </section>
      ) : null}

      {open.length > 1 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-content-muted">Далі сьогодні</h2>
          {open.slice(1).map((item) => (
            <div
              key={item.id}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => void dropOn(item.task_id)}
            >
              <TaskCard
                task={item.task}
                draggable
                onDragStart={() => setDraggedId(item.task_id)}
                onDragEnd={() => setDraggedId(null)}
                trailing={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Виконано"
                    onClick={() => complete.mutate({ id: item.task_id })}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                }
              />
            </div>
          ))}
        </section>
      ) : null}

      {open.length === 0 && !plan?.suggestion_batch ? (
        <EmptyState
          title="На сьогодні все"
          description="Заплановані задачі закриті. Можна видихнути."
        />
      ) : null}

      {closed.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-content-muted">Закрито сьогодні</h2>
          {closed.map((item) => (
            <TaskCard key={item.id} task={item.task} compact />
          ))}
        </section>
      ) : null}

      <TaskComposer
        open={composing}
        defaultDate={today}
        onClose={() => setComposing(false)}
      />
    </div>
  );
}
